import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { Box } from "@mui/material";
import {
  FormType,
  TicketingFormCategory,
  isDrawingFormCategory,
  isVariantActive,
  parseFormBackgroundTheme,
} from "@simplyk/common";

import { FloatingChooseTicketsButton } from "../../../blocks/selection/ticketing-selection/FloatingChooseTicketsButton/FloatingChooseTicketsButton";
import { OccurrenceSelectorProvider } from "../../../blocks/selection/ticketing-selection/OccurrenceSelector/OccurrenceSelectorContext";
import { Prizes } from "../../../blocks/selection/ticketing-selection/Prizes/Prizes";
import { Rates } from "../../../blocks/selection/ticketing-selection/Rates/Rates";
import { TicketingV2AdditionalDonation } from "../../../blocks/selection/ticketing-selection/TicketingV2AdditionalDonation";
import { TicketingWelcomeMessage } from "../../../blocks/selection/ticketing-selection/TicketingWelcomeMessage/TicketingWelcomeMessage";
import { BurstProvider } from "../../../contexts/burstContext";
import { useFormV2Context } from "../../../contexts/FormV2Context";
import { HeartsAnimationProvider } from "../../../contexts/heartsAnimationContext";
import { TicketingCheckoutFooter } from "../shared/CheckoutFooter/TicketingCheckoutFooter";

import { formBackgroundThemeToBurstType } from "@/canvas/components/Burst/formBackgroundThemeToBurstType";
import { MobileCampaignBanner } from "@/canvas/features/FormV2/blocks/campaign-details/CampaignBanner/MobileCampaignBanner";
import { GenerateReceiptInfobox } from "@/canvas/features/FormV2/blocks/payment/GenerateReceiptInfobox";
import { ShopCartDrawer } from "@/canvas/features/FormV2/blocks/payment/ShopCart/ShopCartDrawer";
import { TicketingSummary } from "@/canvas/features/FormV2/blocks/payment/Summary/TicketingSummary";
import { ZeffyBusinessModelInfobox } from "@/canvas/features/FormV2/blocks/payment/ZeffyBusinessModelInfobox";
import { useEnterKeyListener } from "@/canvas/features/FormV2/hooks/useEnterKeyListener";
import { useTrackFormLogoClicked } from "@/canvas/features/FormV2/hooks/useTrackFormLogoClicked";
import { useUtmForm } from "@/canvas/features/FormV2/hooks/useUtmForm";
import { useTicketingFirstStepSubmit } from "@/canvas/features/FormV2/steps/step1/shared/CheckoutFooter/useTicketingFirstStepSubmit";
import { TicketingV2CoreInfo } from "@/canvas/features/FormV2/steps/step1/shared/CoreInfo/TicketingV2CoreInfo";
import { SweepstakesRulesProvider } from "@/canvas/features/FormV2/steps/step1/Sweepstakes/Context/SweepstakesRulesContext";
import { SweepstakesNoDonationInfobox } from "@/canvas/features/FormV2/steps/step1/Sweepstakes/SweepstakesNoDonationInfobox";
import { SweepstakesSection } from "@/canvas/features/FormV2/steps/step1/Sweepstakes/SweepstakesSection";
import { TicketingStep1Layout } from "@/canvas/features/FormV2/steps/step1/Ticketing/TicketingStep1Layout";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useThemedFormZeffyLogoUrl } from "@/common/hooks/useThemedFormZeffyLogoUrl";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { NotificationBannerHeightContext } from "@/components/NotificationBanners/NotificationBannerHeightContext";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
import { FormCartWidget } from "@/forms-design-system/FormCartWidget";
import { FormDivider } from "@/forms-design-system/FormDivider";
import { FormMemberBanner } from "@/forms-design-system/FormMemberBanner";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";
import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";

const SCROLL_THRESHOLD = 100;

export const TicketingStep1: FC = () => {
  const {
    hasBanner,
    description,
    formObject,
    currentStep,
    formCategory,
    bannerAspectRatio,
    isEmbed,
    membershipInfo,
    form,
    formFields,
  } = useFormV2Context(FormType.Ticketing);
  const { isTicketingClosed: ticketingIsClosed } = useFormV2Context(FormType.Ticketing);
  const { isExpanded, bannerHeight } = useContext(NotificationBannerHeightContext);
  const { onFirstStepSubmit, canSubmitFirstStep, totalTickets, additionalDonation } = useTicketingFirstStepSubmit();
  const { isPreview } = usePreviewContext();
  const { logAmplitudeEvent } = useAmplitude();
  const logSweepstakesRulesExpanded = useCallback(
    () =>
      logAmplitudeEvent(AmplitudeEvents.SweepstakesRulesExpanded, {
        ticketingId: formObject.id,
        is_sweepstakes: true,
      }),
    [formObject.id, logAmplitudeEvent]
  );

  const utm = useUtmForm();
  const ratesRef = useRef<HTMLDivElement>(null);

  const burstType = formBackgroundThemeToBurstType(parseFormBackgroundTheme(formObject.backgroundTheme));

  const { shouldDisplayMemberBanner, membershipsToDisplayOnBanner, memberName } = membershipInfo;

  const memberMemberships = useMemo(
    () =>
      membershipsToDisplayOnBanner.map((membership) => ({
        title: membership.membershipType,
        expirationDate: membership.expirationDate,
        isAutoRenewal: membership.automaticRenewal,
      })),
    [membershipsToDisplayOnBanner]
  );

  const isCampaign = !!formObject.thermometerTarget;
  const isOneColumnLayoutV2 = !hasBanner && !description && !isCampaign;
  const isSquareBanner = bannerAspectRatio === "1/1";
  const additionalDonationPosition: "top-of-page" | "default" = ticketingIsClosed ? "top-of-page" : "default";
  const isShop = formCategory === TicketingFormCategory.Shop;
  const hasLanguageSelect = formFields.length > 1;
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const handleOpenCart = useCallback(() => setIsCartDrawerOpen(true), []);
  const handleCloseCart = useCallback(() => setIsCartDrawerOpen(false), []);
  const watchedTicketsPurchased = form.watch("ticketsPurchased");
  const cartItemCount = watchedTicketsPurchased?.reduce((sum, item) => sum + (item?.purchasedNumber || 0), 0) || 0;

  const [hasScrolledPastThreshold, setHasScrolledPastThreshold] = useState(false);
  const [hasEverSelectedTickets, setHasEverSelectedTickets] = useState(false);
  const { isSmallScreen } = useMediaQuery();

  useEffect(() => {
    if (!isSquareBanner) {
      return;
    }

    const scrollContainer =
      document.querySelector<HTMLElement>('[data-test="form-scrollable-area"]') ?? document.documentElement;

    const handleScroll = () => {
      if (scrollContainer.scrollTop > SCROLL_THRESHOLD) {
        setHasScrolledPastThreshold(true);
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    handleScroll();

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [isSquareBanner]);

  useEffect(() => {
    if (totalTickets > 0) {
      setHasEverSelectedTickets(true);
    }
  }, [totalTickets]);

  const shouldShowButton =
    isSquareBanner && isSmallScreen && !hasScrolledPastThreshold && totalTickets === 0 && !hasEverSelectedTickets;

  const onTrackLogoClick = useTrackFormLogoClicked({
    formType: FormType.Ticketing,
    formCategory,
    country: formObject.organization?.country,
    formStep: 1,
  });

  const logoHref = useThemedFormZeffyLogoUrl(utm);

  useEnterKeyListener(onFirstStepSubmit, currentStep === "selection");

  const handleScrollToTickets = () => {
    if (ratesRef.current) {
      ratesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <SweepstakesRulesProvider onRulesExpanded={logSweepstakesRulesExpanded}>
      <OccurrenceSelectorProvider>
        <HeartsAnimationProvider>
          <BurstProvider
            type={burstType}
            brandColorHex={formObject.color}
            canBurstHeart={Boolean(formObject.extraDonation)}
          >
            <TicketingStep1Layout
              contextDrawerHeight={isExpanded ? bannerHeight : 0}
              additionalDonationPosition={additionalDonationPosition}
              isEmbed={isEmbed}
              isOneColumn={isOneColumnLayoutV2}
              showCheckoutFooter={canSubmitFirstStep && !isPreview}
              showMobileChooseTicketsFloatingButton={shouldShowButton}
              logoHref={logoHref}
              onLogoClick={onTrackLogoClick}
              isSquareBanner={isSquareBanner}
              slots={{
                banner: hasBanner ? (
                  <MobileCampaignBanner useSquareRatioOnMobile={Boolean(isSquareBanner)} />
                ) : undefined,
                welcomeBackBanner:
                  !isPreview && shouldDisplayMemberBanner ? (
                    <FormMemberBanner memberName={memberName!} memberships={memberMemberships} />
                  ) : undefined,
                summary: <TicketingSummary />,
                coreInfo: <TicketingV2CoreInfo />,
                welcomeMessage: <TicketingWelcomeMessage />,
                additionalDonation: <TicketingV2AdditionalDonation />,
                prizes: isDrawingFormCategory(formCategory) ? <Prizes /> : undefined,
                rates: (
                  <Box ref={ratesRef}>
                    <Rates />
                  </Box>
                ),
                drawingInfo: isSweepstakesCategory(formCategory) ? <SweepstakesNoDonationInfobox /> : undefined,
                drawingRules: isSweepstakesCategory(formCategory) ? <SweepstakesSection /> : undefined,
                generateReceipt: <GenerateReceiptInfobox />,
                zeffyBusinessModel: <ZeffyBusinessModelInfobox />,
                checkoutFooter: (
                  <TicketingCheckoutFooter hasDonation={additionalDonation > 0} totalTickets={totalTickets} />
                ),
                mobileChooseTicketsFloatingButton: (
                  <FloatingChooseTicketsButton show={shouldShowButton} onClickScrollToTickets={handleScrollToTickets} />
                ),
                divider: !isOneColumnLayoutV2 && !isShop ? <FormDivider sx={{ width: "100%" }} /> : undefined,
                shopCartButton:
                  isVariantActive(formCategory) && cartItemCount > 0 ? (
                    <Box sx={{ mt: hasLanguageSelect ? { xs: "20px", md: "48px" } : 0 }}>
                      <FormCartWidget
                        count={cartItemCount}
                        showCounter
                        onClick={handleOpenCart}
                        data-test="shop-cart-button"
                      />
                    </Box>
                  ) : undefined,
              }}
            />
            {isVariantActive(formCategory) && isShop && (
              <ShopCartDrawer
                open={isCartDrawerOpen}
                onClose={handleCloseCart}
                onCheckout={onFirstStepSubmit}
                canCheckout={canSubmitFirstStep}
              />
            )}
          </BurstProvider>
        </HeartsAnimationProvider>
      </OccurrenceSelectorProvider>
    </SweepstakesRulesProvider>
  );
};
